import type { InlineTextStyle, TextBlockType, TextDocument, TextRun } from '../model/textDocument';
import { createTextId } from '../model/textDocument';
import { normalizeTextDocument } from '../model/textNormalize';

const ZERO_WIDTH_SPACE = '\u200B';

function mergeInlineStyle(base: InlineTextStyle | undefined, patch: InlineTextStyle): InlineTextStyle {
  return { ...(base ?? {}), ...patch };
}

function styleFromElement(element: HTMLElement, inherited?: InlineTextStyle): InlineTextStyle | undefined {
  const tag = element.tagName.toLowerCase();
  let style = inherited;
  if (tag === 'b' || tag === 'strong') style = mergeInlineStyle(style, { bold: true });
  if (tag === 'i' || tag === 'em') style = mergeInlineStyle(style, { italic: true });
  if (tag === 'u') style = mergeInlineStyle(style, { underline: true });
  if (tag === 's' || tag === 'strike' || tag === 'del') style = mergeInlineStyle(style, { strike: true });
  if (tag === 'sup') style = mergeInlineStyle(style, { superscript: true, subscript: undefined });
  if (tag === 'sub') style = mergeInlineStyle(style, { subscript: true, superscript: undefined });
  if (tag === 'a') {
    const href = element.getAttribute('href');
    if (href) style = mergeInlineStyle(style, { link: { href, target: '_blank' } });
  }

  const inline = element.style;
  const patch: InlineTextStyle = {};
  if (inline.color) patch.color = inline.color;
  if (inline.backgroundColor) patch.backgroundColor = inline.backgroundColor;
  if (inline.fontFamily) patch.fontFamily = inline.fontFamily.replace(/^["']|["']$/g, '');
  if (inline.fontWeight) {
    const n = Number(inline.fontWeight);
    patch.fontWeight = Number.isFinite(n) ? n : inline.fontWeight;
  }
  if (inline.fontStyle === 'italic') patch.italic = true;
  if (inline.textDecorationLine.includes('underline')) patch.underline = true;
  if (inline.textDecorationLine.includes('line-through')) patch.strike = true;
  if (inline.fontSize.endsWith('px')) {
    const n = Number.parseFloat(inline.fontSize);
    if (Number.isFinite(n)) patch.fontSize = n;
  }
  return Object.keys(patch).length ? mergeInlineStyle(style, patch) : style;
}

function pushRun(runs: TextRun[], text: string, style?: InlineTextStyle): void {
  const cleanText = text.split(ZERO_WIDTH_SPACE).join('');
  if (!cleanText) return;
  runs.push({
    id: createTextId('run'),
    text: cleanText,
    style,
  });
}

function walkInline(node: Node, runs: TextRun[], style?: InlineTextStyle): void {
  if (node.nodeType === Node.TEXT_NODE) {
    pushRun(runs, node.textContent ?? '', style);
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const element = node as HTMLElement;
  if (element.matches('[data-placeholder], [data-list-marker]')) return;
  if (element.tagName.toLowerCase() === 'br') {
    pushRun(runs, '\n', style);
    return;
  }
  const nextStyle = styleFromElement(element, style);
  element.childNodes.forEach((child) => walkInline(child, runs, nextStyle));
}

function splitRunsByLine(runs: TextRun[]): TextRun[][] {
  const lines: TextRun[][] = [[]];
  for (const run of runs) {
    const parts = run.text.split('\n');
    parts.forEach((part, index) => {
      if (index > 0) lines.push([]);
      if (part) {
        lines[lines.length - 1]!.push({ ...run, id: createTextId('run'), text: part });
      }
    });
  }
  return lines;
}

export function serializeEditableToTextDocument(
  root: HTMLElement,
  blockType: TextBlockType,
): TextDocument {
  const editableBlocks = Array.from(root.querySelectorAll<HTMLElement>('[data-text-edit-block]'));
  if (editableBlocks.length) {
    return normalizeTextDocument({
      version: 1,
      blocks: editableBlocks.map((blockEl) => {
        const runs: TextRun[] = [];
        blockEl.childNodes.forEach((child) => walkInline(child, runs));
        return {
          id: blockEl.dataset.textBlockId || createTextId('block'),
          type: (blockEl.dataset.textBlockType as TextBlockType | undefined) ?? blockType,
          runs,
        };
      }),
    });
  }

  const runs: TextRun[] = [];
  root.childNodes.forEach((child) => walkInline(child, runs));
  const lines = splitRunsByLine(runs);
  return normalizeTextDocument({
    version: 1,
    blocks: (lines.length ? lines : [[]]).map((lineRuns) => ({
      id: createTextId('block'),
      type: blockType,
      runs: lineRuns,
    })),
  });
}
